import { useState, useEffect } from 'react';

export function useLocalStorage<T>(key: string, initialValue: T): [T, (value: T | ((val: T) => T)) => void] {
  const [storedValue, setStoredValue] = useState<T>(() => {
    if (typeof window === 'undefined') {
      return initialValue;
    }

    try {
      const item = window.localStorage.getItem(key);
      return item ? JSON.parse(item) : initialValue;
    } catch {
      return initialValue;
    }
  });

  const setValue = (value: T | ((val: T) => T)) => {
    try {
      const valueToStore = value instanceof Function ? value(storedValue) : value;

      setStoredValue(valueToStore);

      if (typeof window !== 'undefined') {
        window.localStorage.setItem(key, JSON.stringify(valueToStore));
      }
    } catch {
    }
  };

  return [storedValue, setValue];
}

export function useOnboarding() {
  const [hasSeenOnboarding, setHasSeenOnboarding] = useLocalStorage('padel-onboarding-seen', false);
  const [currentStep, setCurrentStep] = useLocalStorage('padel-onboarding-step', 0);

  const completeOnboarding = () => {
    setHasSeenOnboarding(true);
    setCurrentStep(0);
  };

  const resetOnboarding = () => {
    setHasSeenOnboarding(false);
    setCurrentStep(0);
  };

  return {
    hasSeenOnboarding,
    currentStep,
    setCurrentStep,
    completeOnboarding,
    resetOnboarding,
  };
}

export interface GameProgress {
  quizScores: Record<string, number>;
  gamesPlayed: string[];
  completedQuizzes: string[];
  totalPointsEarned: number;
  lastPlayed: string;
}

const defaultProgress: GameProgress = {
  quizScores: {},
  gamesPlayed: [],
  completedQuizzes: [],
  totalPointsEarned: 0,
  lastPlayed: new Date().toISOString(),
};

function getSystemDarkPreference(): boolean {
  try {
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  } catch {
    return false;
  }
}

export function useDarkMode() {
  const systemPreference = getSystemDarkPreference();
  const [isDark, setIsDark] = useLocalStorage('padel-dark-mode', systemPreference);

  useEffect(() => {
    if (isDark) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [isDark]);

  const toggle = () => setIsDark(!isDark);
  return { isDark, toggle };
}

export function useGameProgress() {
  const [progress, setProgress] = useLocalStorage<GameProgress>('padel-game-progress', defaultProgress);

  const updateQuizScore = (quizId: string, score: number) => {
    setProgress(prev => ({
      ...prev,
      quizScores: {
        ...prev.quizScores,
        [quizId]: Math.max(prev.quizScores[quizId] || 0, score),
      },
      completedQuizzes: prev.completedQuizzes.includes(quizId)
        ? prev.completedQuizzes
        : [...prev.completedQuizzes, quizId],
      totalPointsEarned: prev.totalPointsEarned + score,
      lastPlayed: new Date().toISOString(),
    }));
  };

  const updateGameScore = (gameId: string, score: number) => {
    setProgress(prev => ({
      ...prev,
      gamesPlayed: prev.gamesPlayed.includes(gameId)
        ? prev.gamesPlayed
        : [...prev.gamesPlayed, gameId],
      totalPointsEarned: prev.totalPointsEarned + score,
      lastPlayed: new Date().toISOString(),
    }));
  };

  const resetProgress = () => {
    setProgress(defaultProgress);
  };

  return {
    progress,
    updateQuizScore,
    updateGameScore,
    resetProgress,
  };
}
